import { Config } from "@planetscale/database";
import { eq } from "drizzle-orm";
import { Link, redirect } from "react-router";
import { z } from "zod";
import { formatDate } from "~/components/site/Date";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Facts } from "~/components/ui/Facts";
import { InfoBox } from "~/components/ui/InfoBox";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { lstring, tt, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, MetaArgs, OurLoaderArgs } from "~/util/Loaders";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

const Params = z.object({ pageId: z.string() });

async function makeData(args: OurLoaderArgs, cfg: Config) {
  const t = tt.bind(null, args.realm.lang);
  let isOwner = false;
  const sess = args.req.sess;
  const params = Params.parse(args.params);
  if (!params.pageId) {
    return redirect("/pages");
  }

  const page = await pg.db
    .select({
      id: pg.pages.id,
      title: pg.pages.title,
      content: pg.pages.content,
      lang: pg.pages.lang,
      game: pg.pages.game,
      createdAt: pg.pages.createdAt,
      updatedAt: pg.pages.updatedAt,
      user: { id: pg.users.id, login: pg.users.login },
    })
    .from(pg.pages)
    .where(eq(pg.pages.id, parseInt(params.pageId)))
    .innerJoin(pg.users, eq(pg.pages.userId, pg.users.id))
    .then((res) => res.at(0));

  if (!page) {
    throw Response.json(
      { msg: t({ en: "Page not found.", ru: "Страница не найдена." }) },
      { status: 404 }
    );
  }

  // Log
  logPageView(args);
  //

  if (sess && sess.user.id === page.user.id) {
    isOwner = true;
  }

  return { page, isOwner };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

function makeMeta(
  cx: MetaArgs<LoaderData>,
  opts: { title: lstring; canonicalPath?: string }
) {
  return {
    title: `${cx.t({ en: "Pages", ru: "Страницы" })} - ${homePage(cx.realm)}`,

    ogTitle: `L2Hub Pages`,
  };
}

$PAGE({
  path: "pages/:pageId",
  loader: $LOADER?.((la) => {
    return makeData(la, {});
  }),
  meta: (cx) => {
    const { page } = cx.data;
    return makeMeta(cx, {
      title: { en: page.title, ru: page.title },
      canonicalPath: `/pages/${page.id}`,
    });
  },
  render: ({ data }) => {
    const t = useT();
    const turl = useTurl();
    const { page, isOwner } = data;
    const pageTitle = t({ en: page.title, ru: page.title });

    return (
      <div>
        <div className="content">
          <Breadcrumb
            items={[
              {
                title: t({ en: "Pages", ru: "Страницы" }),
                url: turl("/pages"),
              },
              { title: pageTitle, url: turl(`/pages/${page.id}`) },
            ]}
          />
          <PageTitle>{pageTitle}</PageTitle>
          <div className="columns">
            <div className="column is-three-quarters">{page.content}</div>
            <div className="column">
              <InfoBox
                title={t({
                  en: "Information",
                  ru: "Краткая информация",
                })}
              >
                <Facts
                  facts={[
                    {
                      name: t({ en: "Author", ru: "Автоп" }),
                      value: page.user.login,
                    },
                    {
                      name: t({ en: "Lang", ru: "Язык" }),
                      value: page.lang,
                    },
                    {
                      name: t({ en: "Version", ru: "Версия" }),
                      value: page.game,
                    },
                    {
                      name: t({ en: "Created At", ru: "Создано" }),
                      value: formatDate(page.createdAt),
                    },
                  ]}
                />
              </InfoBox>
              <div>
                {isOwner && <Link to={`/pages/${page.id}/edit`}>Edit</Link>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
});
